// client/src/dnd/emitter_tool/engine/engine_parts/sprites/SingleSource.js
import * as PIXI from "pixi.js";

/**
 * SingleSource
 * Emits exactly one texture (the chosen PNG), with robust key resolution.
 * If the chosen name isn't found, it falls back to the first atlas key.
 */
export default class SingleSource {
  /**
   * @param {Object} opts
   * @param {Record<string, PIXI.Texture>} opts.textures
   * @param {string[]} opts.list  // first item used if valid-ish
   */
  constructor({ textures = {}, list = [] } = {}) {
    this.textures = textures || {};
    // keep the user's requested name; resolve lazily each call so late atlas loads work
    this.requested = (Array.isArray(list) && list.length ? String(list[0]) : null);
  }

  // ---- helpers ----
  _keys() {
    try { return Object.keys(this.textures || {}); } catch { return []; }
  }
  _norm(s) { return String(s || "").trim().toLowerCase(); }
  _baseNoExt(str) {
    const slash = Math.max(str.lastIndexOf("/"), str.lastIndexOf("\\"));
    const base = slash >= 0 ? str.slice(slash + 1) : str;
    const dot  = base.lastIndexOf(".");
    return dot > 0 ? base.slice(0, dot) : base;
  }
  _resolveKey(req) {
    const keys = this._keys();
    if (!keys.length) return null;
    if (!req) return keys[0];

    const low = this._norm(req);
    // exact
    let k = keys.find((n) => this._norm(n) === low);
    if (k) return k;
    // basename
    k = keys.find((n) => this._baseNoExt(n).toLowerCase() === low);
    if (k) return k;
    // substring
    k = keys.find((n) => this._norm(n).includes(low));
    return k || keys[0];
  }

  /** @param {number} _dt seconds (ignored for single) */
  next(_dt = 0) {
    const keys = this._keys();
    if (!keys.length) return PIXI.Texture.WHITE; // truly no textures yet

    const key = this._resolveKey(this.requested);
    const t = key ? this.textures[key] : null;

    // If something's weird, still return a real texture if any exist
    return t || this.textures[keys[0]] || PIXI.Texture.WHITE;
  }
}
